// Pure date helpers behind the DatePicker / DateRangePicker controls. Values
// cross the component boundary as ISO "YYYY-MM-DD" strings (what the API and
// the old <input type="date"> spoke); people read and type "DD.MM.YYYY".
import { addDays, isoDate, parseIsoDate } from './time.js';

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
export const WEEKDAY_SHORT = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

// A real calendar day, or null — "2026-02-31" matches the shape but is not one.
export function validIso(value) {
  if (typeof value !== 'string' || !ISO_RE.test(value)) return null;
  return isoDate(parseIsoDate(value)) === value ? value : null;
}

export function formatDisplayDate(iso) {
  const valid = validIso(iso);
  if (!valid) return '';
  const [y, m, d] = valid.split('-');
  return `${d}.${m}.${y}`;
}

// Accepts what people actually type: "01.09.2026", "1.9.2026", "1/9/26",
// "01092026", and "0109" or "1.9." for the current year. Day comes first.
// Returns an ISO string, or null when the text is not a real date.
export function parseDisplayDate(text, today = new Date()) {
  const raw = String(text ?? '').trim();
  if (!raw) return null;
  let day;
  let month;
  let year;
  const parts = raw.split(/[./\s-]+/).filter(Boolean);
  if (parts.length >= 2 && parts.every((part) => /^\d+$/.test(part))) {
    [day, month, year] = parts.map(Number);
    if (parts.length > 3) return null;
    if (year === undefined) year = today.getFullYear();
    else if (parts[2].length <= 2) year += 2000;
  } else {
    const digits = raw.replace(/\D/g, '');
    if (digits !== raw) return null;
    if (digits.length === 8) {
      day = Number(digits.slice(0, 2));
      month = Number(digits.slice(2, 4));
      year = Number(digits.slice(4));
    } else if (digits.length === 4) {
      day = Number(digits.slice(0, 2));
      month = Number(digits.slice(2, 4));
      year = today.getFullYear();
    } else {
      return null;
    }
  }
  if (!year || year < 1000 || year > 9999) return null;
  const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return validIso(iso);
}

// The weeks of one month, Monday first. Each week is seven ISO strings or
// null for the blanks before the 1st and after the last day.
export function monthWeeks(year, month) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  const cells = Array(lead).fill(null);
  for (let day = 1; day <= count; day += 1) {
    cells.push(isoDate(new Date(year, month, day)));
  }
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

// { year, month } shifted by n months.
export function shiftMonth({ year, month }, n) {
  const date = new Date(year, month + n, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
}

export function monthOf(iso, fallback = new Date()) {
  const valid = validIso(iso);
  const date = valid ? parseIsoDate(valid) : fallback;
  return { year: date.getFullYear(), month: date.getMonth() };
}

// Keyboard movement inside a calendar grid: arrows move a day / a week,
// PageUp/PageDown a month (clamped to the target month's length).
export function moveFocusDate(iso, key) {
  const date = parseIsoDate(iso);
  switch (key) {
    case 'ArrowLeft':
      return isoDate(addDays(date, -1));
    case 'ArrowRight':
      return isoDate(addDays(date, 1));
    case 'ArrowUp':
      return isoDate(addDays(date, -7));
    case 'ArrowDown':
      return isoDate(addDays(date, 7));
    case 'PageUp':
    case 'PageDown': {
      const step = key === 'PageUp' ? -1 : 1;
      const target = new Date(date.getFullYear(), date.getMonth() + step, 1);
      const last = new Date(
        target.getFullYear(),
        target.getMonth() + 1,
        0,
      ).getDate();
      target.setDate(Math.min(date.getDate(), last));
      return isoDate(target);
    }
    default:
      return iso;
  }
}

export function outOfBounds(iso, min, max) {
  return Boolean((min && iso < min) || (max && iso > max));
}

// The two clicks of a range pick, in either order, as an ordered pair.
export function orderedRange(a, b) {
  return a <= b ? { from: a, to: b } : { from: b, to: a };
}

// What a range picker's typed From/To fields say: the ordered range, or which
// field is blank or not a real date (`range` is null then).
export function typedRange(fromText, toText, today = new Date()) {
  const from = parseDisplayDate(fromText, today);
  const to = parseDisplayDate(toText, today);
  if (!from || !to) return { range: null, invalid: { from: !from, to: !to } };
  return { range: orderedRange(from, to), invalid: { from: false, to: false } };
}

// Committing one typed field of a range picker (blur or Enter), read against
// what the other field shows: both readable -> the ordered range; this one
// unreadable -> `invalid`; the other blank or unreadable -> just this `date`,
// so the other field's text and flag stay for the user to fix. Mid-pick the
// blank To is the pick's placeholder, so a typed date stands as one day.
export function commitTypedField(
  text,
  otherText,
  picking = false,
  today = new Date(),
) {
  const parsed = parseDisplayDate(text, today);
  if (!parsed) return { invalid: true };
  const other = parseDisplayDate(otherText, today);
  if (other) return { range: orderedRange(parsed, other) };
  if (picking && !String(otherText ?? '').trim())
    return { range: { from: parsed, to: parsed } };
  return { date: parsed };
}
